import React from "react";
import _ from "lodash";
import {
  BooksTwoOutlined,
  PlaceholderFileUpArrowOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";

import ACLStore from "lib/aclStore";
import { MERGE_CUSTOM_DATASETS_INTO_LEARNING_GOALS } from "Constants/permissionConstants";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  ELEMENT_TYPE_DP_ATL,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_UBD_ATL,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  SUBJECT_BASED_GRADING,
  COURSE_BASED_GRADING,
  OLD_SUBJECT_BASED_GRADING,
  ELEMENT_TYPE_PYP_ACTION,
  ELEMENT_TYPE_PYP_KEY_CONCEPT,
  ELEMENT_TYPE_PYP_LEARNER_PROFILE,
  ELEMENT_TYPE_MYP_KEY_CONCEPT,
  ELEMENT_TYPE_MYP_LEARNER_PROFILE,
  ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT,
  ELEMENT_TYPE_DP_LEARNER_PROFILE,
  ELEMENT_TYPE_UBD_SKILL,
} from "Constants/stringConstants";

export const DATASETS_FILTERS_SETTING = "DataSetsFilters";

export const ACADEMIC_SETUP_TYPES = {
  SUBJECT_BASED_GRADING: SUBJECT_BASED_GRADING,
  COURSE_BASED_GRADING: COURSE_BASED_GRADING,
  OLD_SUBJECT_BASED_GRADING: OLD_SUBJECT_BASED_GRADING, // Specific to standards
};

export const STATUS_FILTERS = {
  PUBLISHED: "PUBLISHED",
  DRAFT: "DRAFT",
  NOT_CONFIGURED: "NOT_CONFIGURED",
  PARTIALLY_PUBLISHED: "PARTIALLY_PUBLISHED",
  ARCHIVED: "ARCHIVED",
};

export const IMPORT_FROM_TODDLE_LIBRARY_TYPES = {
  PUBLISHED_STANDARDS: "PUBLISHED_STANDARDS",
  DRAFT_STANDARDS: "DRAFT_STANDARDS",
};

export const MODAL_TYPES = {
  IMPORT_DATASET: "IMPORT_DATASET",
  UPLOAD_DATASET: "UPLOAD_DATASET",
  CREATE_NEW_DATASET: "CREATE_NEW_DATASET",
  RENAME_DATASET: "RENAME_DATASET",
  ARCHIVE_DATASET: "ARCHIVE_DATASET",
  RESTORE_DATASET: "RESTORE_DATASET",
  CREATE_CUSTOM_DATASET: "CREATE_CUSTOM_DATASET",
  SYNC_DATASET: "SYNC_DATASET",
  DUPLICATE_DATASET: "DUPLICATE_DATASET",
};

export const ATL_PLANNER_ELEMENT_SET_TYPES = [
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_DP_ATL,
  ELEMENT_TYPE_UBD_ATL,
];

export const DIALOG_TYPES = {
  DELETE_DATASET: "DELETE_DATASET",
};

export const DIALOG_DETAILS = ({ t, entitySetLabel, title }) => ({
  [DIALOG_TYPES.DELETE_DATASET]: {
    title: t("common:delete_with_label", { label: entitySetLabel }),
    message: t("scopeAndSequence:delete_label_entity_warning", {
      label: entitySetLabel,
      entity: title,
    }),
    buttonOneText: t("common:cancel"),
    buttonTwoText: t("common:delete_with_label", { label: entitySetLabel }),
  },
});

export const DATASET_ACTIONS = {
  RENAME: MODAL_TYPES.RENAME_DATASET,
  ARCHIVE: MODAL_TYPES.ARCHIVE_DATASET,
  DELETE: DIALOG_TYPES.DELETE_DATASET,
  RESTORE: MODAL_TYPES.RESTORE_DATASET,
  TRANSLATE: "TRANSLATE_DATASET",
  SYNC: MODAL_TYPES.SYNC_DATASET,
  DUPLICATE: MODAL_TYPES.DUPLICATE_DATASET,
};

export const PLANNER_ELEMENT_NODE = "PLANNER_ELEMENT_NODE";

export const PLANNER_INTERFACE_TYPES = {
  [ELEMENT_TYPE_PYP_ATL]: "PLANNER_ATL",
  [ELEMENT_TYPE_PYP_LEARNING_STANDARD]: "PLANNER_BENCHMARK",
  [ELEMENT_TYPE_PYP_ACTION]: "ACTION",
  [ELEMENT_TYPE_PYP_LEARNER_PROFILE]: "PLANNER_LEARNER_PROFILE",
  [ELEMENT_TYPE_PYP_KEY_CONCEPT]: "PLANNER_CONCEPT",
};

export const ADD_DROPDOWN_OPTIONS = t => [
  {
    key: MODAL_TYPES.IMPORT_DATASET,
    label: t("scopeAndSequence:import_label_from_library", {
      label: t("scopeAndSequence:standard_set_other"),
    }),
    subText: t("scopeAndSequence:choose_from_range_of_label", {
      label: t("scopeAndSequence:standard_set_other"),
    }),
    icon: <BooksTwoOutlined />,
  },
  {
    key: MODAL_TYPES.UPLOAD_DATASET,
    label: t("scopeAndSequence:bulk_upload_label", {
      label: t("scopeAndSequence:standard_set_other"),
    }),
    subText: t("scopeAndSequence:download_template_file_description"),
    icon: <PlaceholderFileUpArrowOutlined />,
  },
  {
    key: MODAL_TYPES.CREATE_NEW_DATASET,
    label: t("scopeAndSequence:create_standard_set_on_toddle"),
    subText: t("scopeAndSequence:create_subject_standards_interface"),
    icon: <PencilLineOutlined />,
  },
];

export const STATUS_OPTIONS = t => [
  {
    value: STATUS_FILTERS.PUBLISHED,
    label: t("common:published"),
  },
  {
    value: STATUS_FILTERS.PARTIALLY_PUBLISHED,
    label: t("scopeAndSequence:partially_published"),
  },
  {
    value: STATUS_FILTERS.DRAFT,
    label: t("common:draft"),
  },
  {
    value: STATUS_FILTERS.NOT_CONFIGURED,
    label: t("common:not_configured_yet"),
    getIsVisible: ({ isCourseBasedGrading, datasetsGroupType }) => {
      return !(
        //since CBG always creates a draft set, so we will not have any "not configured" status for any set.
        (
          isCourseBasedGrading &&
          datasetsGroupType === DATASETS_GROUP_TYPES.STANDARDS
        )
      );
    },
    getDefaultValue: ({ isCourseBasedGrading, datasetsGroupType }) =>
      !(
        isCourseBasedGrading &&
        datasetsGroupType === DATASETS_GROUP_TYPES.STANDARDS
      ),
  },
  {
    value: STATUS_FILTERS.ARCHIVED,
    label: t("common:archived"),
    getIsVisible: ({ datasetsGroupType }) => {
      return datasetsGroupType === DATASETS_GROUP_TYPES.STANDARDS;
    },
    getDefaultValue: () => false,
  },
];

export const DATASETS_GROUP_TYPES = {
  STANDARDS: "STANDARDS",
  DATASETS: "DATASETS",
};

export const CURRICULUM_PROGRAM_TO_DATASET_GROUPS_MAP = {
  [CURRICULUM_TYPE_PYP]: {
    [DATASETS_GROUP_TYPES.DATASETS]: [
      ELEMENT_TYPE_PYP_ATL,
      ELEMENT_TYPE_PYP_ACTION,
      ELEMENT_TYPE_PYP_KEY_CONCEPT,
      ELEMENT_TYPE_PYP_LEARNER_PROFILE,
    ],
    [DATASETS_GROUP_TYPES.STANDARDS]: [ELEMENT_TYPE_PYP_LEARNING_STANDARD],
  },
  [CURRICULUM_TYPE_MYP]: {
    [DATASETS_GROUP_TYPES.DATASETS]: [
      ELEMENT_TYPE_MYP_ATL,
      ELEMENT_TYPE_MYP_KEY_CONCEPT,
      ELEMENT_TYPE_MYP_LEARNER_PROFILE,
      ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT,
    ],
    [DATASETS_GROUP_TYPES.STANDARDS]: [ELEMENT_TYPE_MYP_LEARNING_STANDARD],
  },
  [CURRICULUM_TYPE_DP]: {
    [DATASETS_GROUP_TYPES.DATASETS]: [
      ELEMENT_TYPE_DP_ATL,
      ELEMENT_TYPE_DP_LEARNER_PROFILE,
    ],
    [DATASETS_GROUP_TYPES.STANDARDS]: [ELEMENT_TYPE_DP_LEARNING_STANDARD],
  },
  [CURRICULUM_TYPE_UBD]: {
    [DATASETS_GROUP_TYPES.DATASETS]: [
      ELEMENT_TYPE_UBD_ATL,
      ELEMENT_TYPE_UBD_SKILL,
    ],
    [DATASETS_GROUP_TYPES.STANDARDS]: [ELEMENT_TYPE_UBD_LEARNING_STANDARD],
  },
};

export const DATASETS_GROUP = _.reduce(
  CURRICULUM_PROGRAM_TO_DATASET_GROUPS_MAP,
  (acc, groups) => {
    _.forEach(_.keys(acc), groupType => {
      _.forEach(groups[groupType], type => acc[groupType].add(type));
    });
    return acc;
  },
  {
    [DATASETS_GROUP_TYPES.STANDARDS]: new Set(),
    [DATASETS_GROUP_TYPES.DATASETS]: new Set(),
  }
);

export const FEEDLIST_COLUMN_IDS = {
  NAME: "NAME",
  COUNT: "COUNT",
  STATUS: "STATUS",
  TAGGED_IN_COURSES: "TAGGED_IN_COURSES",
  LAST_UPDATED_BY: "LAST_UPDATED_BY",
  ACTIONS: "ACTIONS",
  NAVIGATOR: "NAVIGATOR",
  DATASET_TYPE: "DATASET_TYPE",
  UI_TYPE_FOR_UNITS: "UI_TYPE_FOR_UNITS",
};

export const FEEDLIST_UI_TYPES = {
  TEXT_WITH_ICON: "TEXT_WITH_ICON",
  BUTTON: "BUTTON",
  STATUS_ICON: "STATUS_ICON",
  USER_NAME_WITH_AVATAR: "USER_NAME_WITH_AVATAR",
  ACTIONS: "ACTIONS",
  NUMBER_TEXT: "NUMBER_TEXT",
  TAGGED_COURSES_POPOVER: "TAGGED_COURSES_POPOVER",
};

export const FEEDLIST_COLUMN_LIST_BY_GRADING_TYPE = {
  [ACADEMIC_SETUP_TYPES.SUBJECT_BASED_GRADING]: [
    FEEDLIST_COLUMN_IDS.STATUS,
    FEEDLIST_COLUMN_IDS.NAME,
    FEEDLIST_COLUMN_IDS.DATASET_TYPE,
    FEEDLIST_COLUMN_IDS.UI_TYPE_FOR_UNITS,
    FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES,
    FEEDLIST_COLUMN_IDS.COUNT,
    FEEDLIST_COLUMN_IDS.LAST_UPDATED_BY,
    FEEDLIST_COLUMN_IDS.ACTIONS,
  ],
  [ACADEMIC_SETUP_TYPES.COURSE_BASED_GRADING]: [
    FEEDLIST_COLUMN_IDS.STATUS,
    FEEDLIST_COLUMN_IDS.NAME,
    FEEDLIST_COLUMN_IDS.DATASET_TYPE,
    FEEDLIST_COLUMN_IDS.UI_TYPE_FOR_UNITS,
    FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES,
    FEEDLIST_COLUMN_IDS.COUNT,
    FEEDLIST_COLUMN_IDS.LAST_UPDATED_BY,
    FEEDLIST_COLUMN_IDS.ACTIONS,
  ],
  [ACADEMIC_SETUP_TYPES.OLD_SUBJECT_BASED_GRADING]: [
    FEEDLIST_COLUMN_IDS.STATUS,
    FEEDLIST_COLUMN_IDS.NAME,
    FEEDLIST_COLUMN_IDS.DATASET_TYPE,
    FEEDLIST_COLUMN_IDS.UI_TYPE_FOR_UNITS,
    FEEDLIST_COLUMN_IDS.COUNT,
    FEEDLIST_COLUMN_IDS.LAST_UPDATED_BY,
    FEEDLIST_COLUMN_IDS.ACTIONS,
  ],
};
const ALL_DATASET_TYPES = [
  DATASETS_GROUP_TYPES.STANDARDS,
  DATASETS_GROUP_TYPES.DATASETS,
];

export const FEEDLIST_COLUMN_LIST_CONFIG = {
  [FEEDLIST_COLUMN_IDS.NAME]: {
    datasetsGroupTypes: ALL_DATASET_TYPES,
  },
  [FEEDLIST_COLUMN_IDS.COUNT]: {
    datasetsGroupTypes: ALL_DATASET_TYPES,
  },
  [FEEDLIST_COLUMN_IDS.STATUS]: {
    datasetsGroupTypes: ALL_DATASET_TYPES,
  },
  [FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES]: {
    datasetsGroupTypes: [DATASETS_GROUP_TYPES.STANDARDS],
  },
  [FEEDLIST_COLUMN_IDS.LAST_UPDATED_BY]: {
    datasetsGroupTypes: ALL_DATASET_TYPES,
  },
  [FEEDLIST_COLUMN_IDS.ACTIONS]: {
    datasetsGroupTypes: ALL_DATASET_TYPES,
  },
  [FEEDLIST_COLUMN_IDS.NAVIGATOR]: {
    datasetsGroupTypes: ALL_DATASET_TYPES,
  },
  [FEEDLIST_COLUMN_IDS.DATASET_TYPE]: {
    getIsVisible: ({ datasetsGroupType }) =>
      ACLStore.can(MERGE_CUSTOM_DATASETS_INTO_LEARNING_GOALS) &&
      datasetsGroupType === DATASETS_GROUP_TYPES.DATASETS,
  },
  [FEEDLIST_COLUMN_IDS.UI_TYPE_FOR_UNITS]: {
    getIsVisible: ({ datasetsGroupType }) =>
      ACLStore.can(MERGE_CUSTOM_DATASETS_INTO_LEARNING_GOALS) &&
      datasetsGroupType === DATASETS_GROUP_TYPES.DATASETS,
  },
};

export const FEEDLIST_COLUMN_LIST = ({ t, isSubjectGroupingAllowed }) => ({
  [FEEDLIST_COLUMN_IDS.NAME]: {
    id: FEEDLIST_COLUMN_IDS.NAME,
    type: "CUSTOM",
    label: t("common:name"),
    uiType: FEEDLIST_UI_TYPES.TEXT_WITH_ICON,
    style: {
      minWidth: "240px",
      maxWidth: "240px",
      width: "100%",
    },
    ...(!isSubjectGroupingAllowed
      ? {
          sortType: "toggle",
          entity: "TITLE",
        }
      : {}),
  },
  [FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES]: {
    id: FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES,
    type: "CUSTOM",
    label: t("scopeAndSequence:tagged_in_courses"),
    uiType: FEEDLIST_UI_TYPES.TAGGED_COURSES_POPOVER,
    style: {
      width: "120px",
    },
  },
  [FEEDLIST_COLUMN_IDS.COUNT]: {
    id: FEEDLIST_COLUMN_IDS.COUNT,
    type: "CUSTOM",
    label: t("common:no_of_label", {
      label: t("common:items"),
    }),
    uiType: FEEDLIST_UI_TYPES.NUMBER_TEXT,
    style: {
      width: "80px",
    },
  },
  [FEEDLIST_COLUMN_IDS.STATUS]: {
    id: FEEDLIST_COLUMN_IDS.STATUS,
    type: "CUSTOM",
    label: t("common:status"),
    uiType: FEEDLIST_UI_TYPES.STATUS_ICON,
    style: {
      width: "40px",
      paddingLeft: "12px",
    },
  },
  [FEEDLIST_COLUMN_IDS.LAST_UPDATED_BY]: {
    id: FEEDLIST_COLUMN_IDS.LAST_UPDATED_BY,
    label: t("scopeAndSequence:last_updated_by"),
    type: "CUSTOM",
    uiType: FEEDLIST_UI_TYPES.USER_NAME_WITH_AVATAR,
    style: {
      width: "220px",
    },
  },
  [FEEDLIST_COLUMN_IDS.ACTIONS]: {
    id: FEEDLIST_COLUMN_IDS.ACTIONS,
    label: t("common:action_plural"),
    type: "CUSTOM",
    uiType: FEEDLIST_UI_TYPES.ACTIONS,
    style: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "88px",
    },
  },
  [FEEDLIST_COLUMN_IDS.NAVIGATOR]: {
    id: FEEDLIST_COLUMN_IDS.NAVIGATOR,
    label: "",
    type: "CUSTOM",
    uiType: FEEDLIST_UI_TYPES.BUTTON,
    style: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "72px",
    },
  },
  [FEEDLIST_COLUMN_IDS.DATASET_TYPE]: {
    id: FEEDLIST_COLUMN_IDS.DATASET_TYPE,
    type: "CUSTOM",
    label: t("common:type"),
    uiType: FEEDLIST_UI_TYPES.TEXT_WITH_ICON,
    style: {
      width: "140px",
    },
  },
  [FEEDLIST_COLUMN_IDS.UI_TYPE_FOR_UNITS]: {
    id: FEEDLIST_COLUMN_IDS.UI_TYPE_FOR_UNITS,
    type: "CUSTOM",
    label: t("scopeAndSequence:ui_type_for_units"),
    uiType: FEEDLIST_UI_TYPES.TEXT_WITH_ICON,
    style: {
      width: "140px",
    },
  },
});

export const CUSTOM_DATASET_UI_TYPES = {
  CARD: "CARD",
  CHECKLIST: "CHECKLIST",
  NESTED_CHECKLIST: "NESTED_CHECKLIST",
};

export const PLANNER_ELEMENT_NODE_SET_CONFIG_PROPERTIES = {
  UI_TYPE: "uiType",
  GRADING_PREFERENCE: "gradingPreference",
  ALLOW_ICON: "allowIcon",
  ALLOW_SUBJECT_GROUPS: "allowSubjectGroups",
  ALLOW_CODE: "allowCode",
  ENABLE_GRADING: "enableGrading",
  ALLOW_GRADES: "allowGrades",
  ALLOW_SUBJECTS: "allowSubjects",
  ALLOW_TAGS: "allowTags",
  ALLOW_DESCRIPTION: "allowDescription",
  IS_TAGGED_IN_ASSESSMENT_ALLOWED: "isTaggedInAssessmentAllowed",
  IS_TAGGED_IN_POST_ALLOWED: "isTaggedInPostAllowed",
  IS_TAGGED_IN_UNIT_PLAN_ALLOWED: "isTaggedInUnitPlanAllowed",
};
